import { useCallback, useState } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { isValidAdminRole } from "@/lib/utils/roles";

export interface UseUpdateActiveRoleReturn {
  updateRole: (role: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export function useUpdateActiveRole(): UseUpdateActiveRoleReturn {
  const { update } = useSession();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateRole = useCallback(
    async (role: string) => {
      if (!isValidAdminRole(role)) {
        const errorMsg = `Invalid role: ${role}. Must be one of Admin, Teacher, or Staff.`;
        setError(errorMsg);
        throw new Error(errorMsg);
      }

      setIsLoading(true);
      setError(null);

      try {
        await update({
          activeRole: role,
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to update role. Please try again.";
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [update, router],
  );

  return {
    updateRole,
    isLoading,
    error,
  };
}